import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * The one line a dashboard widget puts above its content.
 *
 * WHY THIS EXISTS (2026-08-05)
 * ---------------------------
 * `Dashboard.tsx` renders every widget's TITLE from one shared bar, but the line
 * under it — the "3 pending" / "12 events this week" summary — was hand-rolled
 * inside each widget. Thirteen copies, measured:
 *
 *   * font size: `caption` in ten, `body2` in three;
 *   * weight: absent in seven, 500 in two, 600 in four;
 *   * colour: `text.secondary` in eleven, and the DEPRECATED `textSecondary`
 *     form in two;
 *   * gap to the content below: `mb: 1` in exactly two, and nothing in eleven.
 *
 * That last one is what reads as misalignment on screen: identical-looking lines
 * where two reserve 8px underneath and eleven reserve none, so the first row of
 * real content sits at a different height in adjacent cards.
 *
 * This is the defect `svdy-rules` §10.5b records — "copy-pasted chrome … four
 * different bottom margins, so each card's first content row sat at a different
 * height" — on the same dashboard, at a larger scale. Extracting is the fix the
 * rule prescribes; a pass that normalised thirteen copies to matching values
 * would drift again on the fourteenth widget.
 *
 * THE `action` SLOT IS ALWAYS RESERVED
 * `minHeight` holds the row at the height of a small Chip whether or not one is
 * rendered, so a widget whose count is currently zero does not sit 20px higher
 * than the card beside it. That is svdy-ui §11.3's rule about an optional line:
 * reserve the space unconditionally, or two cards in a row lose their shared
 * baseline the moment one of them has nothing to show.
 *
 * NOT for every widget. Cards that open with a KPI row (`ProcessSLAStatus`) or
 * straight into a metric grid (`AccessControlOverview`, `AttendanceSummary`) have
 * a different first row on purpose, and forcing this on them would invent a
 * subtitle they never had.
 */
const WidgetSummaryLine: React.FC<{
  /** The summary text — normally a `{{count}}` interpolation. */
  children: React.ReactNode;
  /** Optional right-hand element, e.g. an "overdue" Chip. */
  action?: React.ReactNode;
}> = ({ children, action }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 1,
      // ONE content axis with the rows below (svdy-ui §15, token in `shellMetrics`).
      //
      // This line used to sit at the body's own padding edge while the list rows beneath it added
      // their own `px`, so the two elements the eye most directly compares — a heading and the
      // first row under it — started 8px or 12px apart depending on the widget. Reported
      // 2026-09-02 as "不对齐" on Process approvals.
      // Height of a `size="small"` Chip, so the row does not collapse when
      // `action` is absent. See the note above.
      minHeight: 20,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
    {action}
  </Box>
);

export default WidgetSummaryLine;
